import { useRef } from "react";
import { useClassNames } from "@/hooks/useClassNames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleRight } from "@fortawesome/free-solid-svg-icons";
import { MenuItem } from "@/types/domain";
import { HeaderTip, NAV_TIP_DELAY } from "../HeaderTip";
import { MenuIcon } from "./MenuIcon";
import { NavFlyout } from "./NavFlyout";
import { useNavPanel, SETTINGS_PANEL_ID } from "./NavPanelContext";
import { useMenuNavigation } from "./useMenuNavigation";

const MiniTip = ({ isMini, label, children }: { isMini: boolean; label: string; children: React.ReactElement }) =>
  isMini ? <HeaderTip label={label} side="right" delayDuration={NAV_TIP_DELAY}>{children}</HeaderTip> : children;

export type MenuItemRowProps = {
  item: MenuItem;
  isMini: boolean;
  onOpenSettings?: () => void;
  pinned?: boolean;
}

const PINNED_ROW_CLASS = "sticky top-0 z-10 -ml-2.5 pl-2.5 pt-5 bg-(--layout-side-nav-bg) pb-2 border-b border-white/15";

const clickOnKey = (e: React.KeyboardEvent<HTMLElement>) => {
  if (e.key !== "Enter" && e.key !== " ") return;
  e.preventDefault();
  e.currentTarget.click();
};

const hasSelectedDescendant = (item: MenuItem): boolean =>
  item.children?.some((c) => c.isSelected || hasSelectedDescendant(c)) === true;

export const MenuItemRow = ({ item, isMini, onOpenSettings, pinned }: MenuItemRowProps) => {
  const liClass = `menu-item mb-0${pinned ? ` ${PINNED_ROW_CLASS}` : ""}`;
  const triggerRef = useRef<HTMLDivElement>(null);
  const panel = useNavPanel();
  const { handlePopupClick: onPopupClick, handleLinkClick: onLinkClick } = useMenuNavigation();

  if (!item.isVisible || (item.type === "settings" && !onOpenSettings)) return null;

  const isSettings = item.type === "settings";
  const isExpandable = item.type === "dropdown" || item.type === "favourites" || item.type === "pipelines";
  const rounded = isMini ? "rounded-lg" : "rounded-l-2xl";
  const rowFocusClass = `outline-none ${rounded} focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white/80`;
  const isOpen = (isExpandable && panel.activeId === item.id) || (isSettings && panel.activeId === SETTINGS_PANEL_ID);
  const hasSelectedChild = isExpandable && hasSelectedDescendant(item);
  const highlighted = item.isSelected || hasSelectedChild;

  const selectedClass = isOpen
    ? `bg-white text-black ${rounded}`
    : highlighted
      ? `text-white bg-[var(--layout-side-nav-active-bg)] ${rounded}`
      : `text-[var(--layout-side-nav-text)] hover:bg-white hover:text-black ${rounded}`;

  const iconClass = useClassNames("shrink-0", !highlighted && !isOpen && "group-hover:text-[var(--layout-side-nav-active-bg)]", isOpen && "text-[var(--layout-side-nav-active-bg)]");

  const icon = item.icon ? <MenuIcon name={item.icon} className={iconClass} /> : null;

  const labelEl = !isMini && (
    <span className={useClassNames("truncate flex-1", hasSelectedChild && "font-bold")}>{item.label}</span>
  );

  const badgeEl = !isMini && item.badge && (
    <span
      className={useClassNames(
        "shrink-0 rounded-full px-1.5 py-px text-[10px] font-semibold         uppercase leading-4",
        item.badge.toLowerCase() === "beta"
                  ? useClassNames("text-white border border-white/60 bg-transparent", (!highlighted || isOpen) && "group-hover:border-[var(--layout-side-nav-bg)] group-hover:text-[var(--layout-side-nav-bg)]", isOpen && "border-[var(--layout-side-nav-bg)] text-[var(--layout-side-nav-bg)]")
          : highlighted && !isOpen
                    ? "bg-white text-[var(--layout-side-nav-active-bg)]"
                    : "text-white bg-[var(--layout-side-nav-active-bg)]",
      )}
    >
      {item.badge}
    </span>
  );

  const chevronEl = !isMini && (isExpandable || isSettings) && <FontAwesomeIcon icon={faAngleRight} className="shrink-0 -translate-y-px opacity-50" />;

  const rowInner = (
    <div
      className={useClassNames(
        "group flex items-center gap-2 cursor-pointer transition-colors",
        isMini ? "mx-2 min-h-10 justify-center rounded-lg" : "py-[7px] [@media(max-height:1000px)]:py-[5px] rounded-l-2xl",
        selectedClass,
      )}
      style={isMini ? undefined : { paddingLeft: "16px", paddingRight: "10px" }}
    >
      {icon}
      {labelEl}
      {badgeEl}
      {chevronEl}
    </div>
  );

  const toggleFlyout = () => panel.toggle(item.id);
  const closeFlyout = () => panel.close(item.id);
  const returnFocus = () => triggerRef.current?.focus();

  const handlePopupClick = (e: React.MouseEvent) => onPopupClick(e, item);
  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => onLinkClick(e, item);

  if (isExpandable) {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === "ArrowRight" && !isOpen) {
        e.preventDefault();
        toggleFlyout();
        return;
      }
      clickOnKey(e);
    };

    return (
      <li className={liClass}>
        <MiniTip isMini={isMini} label={item.label}>
        <div
          ref={triggerRef}
          role="button"
          tabIndex={0}
          className={rowFocusClass}
          data-testid={item.testId}
          data-nav-flyout-trigger={item.id}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={isMini ? item.label : undefined}
          onClick={toggleFlyout}
          onKeyDown={handleKeyDown}
        >
          {rowInner}
        </div>
        </MiniTip>
        <NavFlyout item={item} open={isOpen} onClose={closeFlyout} onReturnFocus={returnFocus} />
      </li>
    );
  }

  const rowEl = isSettings
    ? <div role="button" tabIndex={0} aria-haspopup="dialog" aria-expanded={isOpen} aria-label={isMini ? item.label : undefined} className={rowFocusClass} data-testid="settings-menu-button" onClick={onOpenSettings} onKeyDown={clickOnKey}>{rowInner}</div>
    : item.type === "link"
    ? <a href={item.url ?? "#"} className={`block ${rowFocusClass}`} data-testid={item.testId} onClick={handleLinkClick}>{rowInner}</a>
    : <div role="button" tabIndex={0} className={rowFocusClass} data-testid={item.testId} onClick={handlePopupClick} onKeyDown={clickOnKey}>{rowInner}</div>;

  return (
    <li className={liClass}>
      <MiniTip isMini={isMini} label={item.label}>{rowEl}</MiniTip>
    </li>
  );
}
